import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import OwnerPageLayout from '../../components/layout/OwnerPageLayout';
import { getApiBaseUrl } from '../../apiBase';
import './activity-log.css';

function adminApi() {
    return axios.create({
        baseURL: getApiBaseUrl(),
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    });
}

const ACTION_LABELS = {
    create_student: 'Создание ученика',
    create_staff: 'Создание тренера',
    invite_sent: 'Приглашение',
    invite_resend: 'Повтор приглашения',
    invite_accept: 'Принятие приглашения',
    activate: 'Активация',
    login: 'Вход',
    register: 'Регистрация',
    create_school: 'Создание школы',
    payment_created: 'Платёж',
    create_schedule_slot: 'Слот расписания',
    exception: 'Исключение',
};

function formatTime(iso) {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleString();
    } catch {
        return iso;
    }
}

function getActionLabel(action) {
    return ACTION_LABELS[action] || action;
}

function getPlatformEmoji(platform) {
    if (platform === 'android') return '🤖';
    if (platform === 'ios') return '🍎';
    return '🌐';
}

function DetailRow({ label, value }) {
    return (
        <>
            <dt>{label}</dt>
            <dd>{value ?? '—'}</dd>
        </>
    );
}

export default function ActivityLogPage() {
    const [logs, setLogs] = useState([]);
    const [total, setTotal] = useState(0);
    const [stats, setStats] = useState(null);
    const [filters, setFilters] = useState({
        status: '',
        action: '',
        platform: '',
        search: '',
        from: '',
        to: '',
    });
    const [page, setPage] = useState(1);
    const [selectedLog, setSelectedLog] = useState(null);
    const [fullLog, setFullLog] = useState(null);
    const [loading, setLoading] = useState(false);
    const [msg, setMsg] = useState('');

    const pageSize = 50;

    const loadStats = useCallback(async () => {
        try {
            const res = await adminApi().get('/admin/activity-log/stats');
            setStats(res.data);
        } catch {
            // ignore
        }
    }, []);

    const loadLogs = useCallback(async () => {
        setLoading(true);
        setMsg('');
        try {
            const params = { page, pageSize };
            if (filters.status) params.status = filters.status;
            if (filters.action) params.action = filters.action;
            if (filters.platform) params.platform = filters.platform;
            if (filters.search) params.search = filters.search;
            if (filters.from) params.from = new Date(filters.from).toISOString();
            if (filters.to) params.to = new Date(filters.to + 'T23:59:59').toISOString();

            const res = await adminApi().get('/admin/activity-log', { params });
            setLogs(res.data?.items || []);
            setTotal(res.data?.total || 0);
        } catch (e) {
            setMsg(e.response?.data?.message || 'Ошибка загрузки журнала');
        } finally {
            setLoading(false);
        }
    }, [filters, page, pageSize]);

    useEffect(() => {
        loadStats();
    }, [loadStats]);

    useEffect(() => {
        loadLogs();
    }, [loadLogs]);

    const loadFullLog = async (id) => {
        try {
            const res = await adminApi().get(`/admin/activity-log/${id}`);
            setFullLog(res.data);
        } catch (e) {
            setMsg(e.response?.data?.message || 'Не удалось загрузить детали');
        }
    };

    const successCount = stats?.byStatus?.find((s) => s.status === 'success')?.count ?? 0;
    const errorCount = stats?.byStatus?.find((s) => s.status === 'error')?.count ?? 0;

    return (
        <OwnerPageLayout title="📋 Журнал действий">
            <div className="al-stats-row">
                <div className="al-stat-card">
                    <div className="al-stat-label">Успешных</div>
                    <div className="al-stat-value" style={{ color: '#16a34a' }}>{successCount}</div>
                </div>
                <div className="al-stat-card">
                    <div className="al-stat-label">Ошибок (7 дн.)</div>
                    <div className="al-stat-value" style={{ color: '#dc2626' }}>{errorCount}</div>
                </div>
                <div className="al-stat-card">
                    <div className="al-stat-label">Всего запросов</div>
                    <div className="al-stat-value">{stats?.totalRequests ?? 0}</div>
                </div>
                <div className="al-stat-card">
                    <div className="al-stat-label">% ошибок</div>
                    <div className="al-stat-value">{(stats?.errorRate ?? 0).toFixed(1)}%</div>
                </div>
            </div>

            {stats?.errors?.length > 0 && (
                <div className="al-error-summary">
                    <h3 style={{ margin: '0 0 8px' }}>⚠️ Топ ошибок за 7 дней</h3>
                    {stats.errors.slice(0, 5).map((e) => (
                        <div key={e.code || 'null'} className="al-error-item">
                            <span className="al-error-code">{e.code || '—'}</span>
                            <span className="al-error-count">{e.count}×</span>
                            <span className="al-small">{formatTime(e.lastSeen)}</span>
                            <button
                                type="button"
                                onClick={() => {
                                    setFilters((f) => ({ ...f, status: 'error', search: e.code || '' }));
                                    setPage(1);
                                }}
                            >
                                Показать
                            </button>
                        </div>
                    ))}
                </div>
            )}

            <div className="al-filters">
                <input
                    placeholder="Поиск по имени, IP, ошибке..."
                    value={filters.search}
                    onChange={(e) => { setFilters({ ...filters, search: e.target.value }); setPage(1); }}
                />
                <select value={filters.status} onChange={(e) => { setFilters({ ...filters, status: e.target.value }); setPage(1); }}>
                    <option value="">Все статусы</option>
                    <option value="success">✅ Успех</option>
                    <option value="error">❌ Ошибка</option>
                    <option value="warning">⚠️ Предупреждение</option>
                </select>
                <select value={filters.platform} onChange={(e) => { setFilters({ ...filters, platform: e.target.value }); setPage(1); }}>
                    <option value="">Все платформы</option>
                    <option value="android">🤖 Android</option>
                    <option value="ios">🍎 iOS</option>
                    <option value="web">🌐 Web</option>
                </select>
                <select value={filters.action} onChange={(e) => { setFilters({ ...filters, action: e.target.value }); setPage(1); }}>
                    <option value="">Все действия</option>
                    <option value="create_student">Создание ученика</option>
                    <option value="invite_sent">Приглашение</option>
                    <option value="login">Вход</option>
                    <option value="create_staff">Создание тренера</option>
                    <option value="exception">Исключения</option>
                </select>
                <input type="date" value={filters.from} onChange={(e) => { setFilters({ ...filters, from: e.target.value }); setPage(1); }} />
                <input type="date" value={filters.to} onChange={(e) => { setFilters({ ...filters, to: e.target.value }); setPage(1); }} />
                <button
                    type="button"
                    onClick={() => {
                        setFilters({ status: '', action: '', platform: '', search: '', from: '', to: '' });
                        setPage(1);
                    }}
                >
                    Сбросить
                </button>
            </div>

            {msg && <p style={{ color: '#dc2626' }}>{msg}</p>}
            {loading && <p>Загрузка...</p>}

            <div className="al-table-wrap">
                <table className="al-table">
                    <thead>
                        <tr>
                            <th>Время</th>
                            <th>Пользователь</th>
                            <th>Действие</th>
                            <th>Сущность</th>
                            <th>Статус</th>
                            <th>Платформа</th>
                            <th>IP</th>
                            <th>Мс</th>
                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        {logs.map((log) => (
                            <tr key={log.id} className={log.status === 'error' ? 'row-error' : ''}>
                                <td className="al-small">{formatTime(log.createdAt)}</td>
                                <td>
                                    <div>{log.userName || '—'}</div>
                                    <div className="al-small">{log.userRole}</div>
                                </td>
                                <td>
                                    <span className={`al-badge action-${log.action}`}>
                                        {getActionLabel(log.action)}
                                    </span>
                                </td>
                                <td>
                                    <div>{log.entityType}</div>
                                    <div className="al-small">{log.entityName}</div>
                                </td>
                                <td>
                                    <span className={`al-badge status-${log.status}`}>
                                        {log.status === 'success' ? '✅' : log.status === 'error' ? '❌' : '⚠️'}
                                        {' '}
                                        {log.status}
                                    </span>
                                    {log.errorCode && <div className="al-small">{log.errorCode}</div>}
                                </td>
                                <td>
                                    {getPlatformEmoji(log.platform)}
                                    {log.appVersion && <span className="al-small"> v{log.appVersion}</span>}
                                </td>
                                <td className="al-small">{log.ipAddress}</td>
                                <td className={log.durationMs > 2000 ? 'slow' : ''}>{log.durationMs}ms</td>
                                <td>
                                    <button type="button" onClick={() => { setSelectedLog(log); setFullLog(null); }}>
                                        🔍
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div style={{ marginTop: 12, display: 'flex', gap: 8, alignItems: 'center' }}>
                <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>←</button>
                <span>Стр. {page} из {Math.max(1, Math.ceil(total / pageSize))} ({total})</span>
                <button type="button" disabled={page * pageSize >= total} onClick={() => setPage((p) => p + 1)}>→</button>
            </div>

            {selectedLog && (
                <div className="al-modal-overlay" onClick={() => setSelectedLog(null)}>
                    <div className="al-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="al-modal-header">
                            <h3 style={{ margin: 0 }}>Детали #{selectedLog.id}</h3>
                            <button type="button" onClick={() => setSelectedLog(null)}>✕</button>
                        </div>

                        <dl className="al-detail-grid">
                            <DetailRow label="Время" value={formatTime(selectedLog.createdAt)} />
                            <DetailRow label="Пользователь" value={`${selectedLog.userName || '—'} (${selectedLog.userId ?? '—'})`} />
                            <DetailRow label="Роль" value={selectedLog.userRole} />
                            <DetailRow label="Школа" value={selectedLog.schoolId} />
                            <DetailRow label="IP" value={selectedLog.ipAddress} />
                            <DetailRow label="Платформа" value={`${selectedLog.platform || '—'} ${selectedLog.appVersion || ''}`} />
                            <DetailRow label="Устройство" value={selectedLog.deviceType} />
                            <DetailRow label="Действие" value={getActionLabel(selectedLog.action)} />
                            <DetailRow label="Сущность" value={`${selectedLog.entityType} #${selectedLog.entityId ?? '—'} ${selectedLog.entityName || ''}`} />
                            <DetailRow label="HTTP" value={`${selectedLog.requestMethod} ${selectedLog.requestPath}`} />
                            <DetailRow label="Статус ответа" value={selectedLog.responseStatusCode} />
                            <DetailRow label="Время" value={`${selectedLog.durationMs}ms`} />
                        </dl>

                        {selectedLog.errorMessage && (
                            <div style={{ marginBottom: 12 }}>
                                <h4>❌ {selectedLog.errorCode}</h4>
                                <p>{selectedLog.errorMessage}</p>
                                {selectedLog.hasError && (
                                    <button type="button" onClick={() => loadFullLog(selectedLog.id)}>
                                        Показать стек и тело запроса
                                    </button>
                                )}
                            </div>
                        )}

                        {fullLog?.requestBody && (
                            <div style={{ marginBottom: 12 }}>
                                <h4>📤 Запрос</h4>
                                <pre className="al-pre">{fullLog.requestBody}</pre>
                            </div>
                        )}

                        {fullLog?.errorDetails && (
                            <div style={{ marginBottom: 12 }}>
                                <h4>Стек</h4>
                                <pre className="al-pre">{fullLog.errorDetails}</pre>
                            </div>
                        )}

                        {fullLog?.context && (
                            <div>
                                <h4>📋 Контекст</h4>
                                <pre className="al-pre">{fullLog.context}</pre>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </OwnerPageLayout>
    );
}
